import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Download,
  Loader2,
  LogOut,
  MoreVertical,
  Plug,
  RefreshCw,
  ScrollText,
  SlidersHorizontal,
} from "lucide-react";
import { formatCompactNumber, formatDate } from "@/lib/api";

function formatUsdCompact(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function StatusBadge({ syncState, apiKeyState }) {
  const running = syncState.running;
  const status = syncState.latest?.status;
  let dotColor = "bg-black/30";
  let label = "IDLE";
  if (!apiKeyState.connected) {
    dotColor = "bg-black/30";
    label = "DISCONNECTED";
  } else if (running) {
    dotColor = "bg-[#007AFF] animate-pulse";
    label = "SYNCING";
  } else if (status === "success") {
    dotColor = "bg-[#16A34A]";
    label = "SYNCED";
  } else if (status === "failed") {
    dotColor = "bg-[#DC2626]";
    label = "FAILED";
  } else if (status === "partial") {
    dotColor = "bg-[#CA8A04]";
    label = "PARTIAL";
  } else if (apiKeyState.connected) {
    dotColor = "bg-[#16A34A]";
    label = "CONNECTED";
  }
  return (
    <div className="flex items-center gap-2 flex-shrink-0" data-testid="status-badge">
      <div className={`w-2 h-2 rounded-full ${dotColor}`} />
      <span className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/70 whitespace-nowrap">
        {label}
      </span>
    </div>
  );
}

function Stat({ label, value, testid, valueClass = "" }) {
  return (
    <div className="flex flex-col flex-shrink-0" data-testid={testid}>
      <span className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/45 whitespace-nowrap">
        {label}
      </span>
      <span
        className={`font-mono-plex text-xs ${valueClass || "text-black"} tabular-nums whitespace-nowrap`}
      >
        {value}
      </span>
    </div>
  );
}

export default function TopBar({
  apiKeyState,
  syncState,
  creditStats,
  onConnectApi,
  onSync,
  onExportAll,
  onExportFiltered,
  onExportSelected,
  onOpenLogs,
  onOpenMobileFilters,
  onLogout,
  selectedCount,
  filterActive,
}) {
  const latest = syncState.latest;
  const included = creditStats?.included_usage_usd;
  const remaining = creditStats?.balance_remaining_usd;
  const remainingPct =
    included && remaining !== null && remaining !== undefined && included > 0
      ? (remaining / included) * 100
      : null;
  const remainingColor =
    remainingPct === null
      ? ""
      : remainingPct <= 10
        ? "text-[#DC2626]"
        : remainingPct <= 25
          ? "text-[#CA8A04]"
          : "text-[#16A34A]";

  return (
    <header
      className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-black/10"
      data-testid="top-bar"
    >
      <div className="flex items-center justify-between gap-2 px-3 md:px-6 py-3">
        {/* Brand + status */}
        <div className="flex items-center gap-3 md:gap-6 min-w-0">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 border border-black/25 grid place-items-center flex-shrink-0">
              <div className="w-2 h-2 bg-black" />
            </div>
            <div className="flex flex-col leading-tight min-w-0">
              <span className="font-heading text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-black whitespace-nowrap">
                Convo · Export
              </span>
              <span className="hidden md:inline font-mono-plex text-[10px] uppercase tracking-[0.3em] text-black/45 whitespace-nowrap">
                elevenlabs · workspace mirror
              </span>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-6 pl-6 border-l border-black/10">
            <StatusBadge syncState={syncState} apiKeyState={apiKeyState} />
            <Stat
              label="Last sync"
              value={
                latest?.completed_at
                  ? formatDate(latest.completed_at)
                  : latest?.started_at
                    ? formatDate(latest.started_at)
                    : "never"
              }
              testid="last-sync-timestamp-wrapper"
            />
            <Stat
              label="Records"
              value={syncState.total_conversations ?? 0}
              testid="records-count-wrapper"
            />
            <Stat
              label="Credits left"
              value={formatCompactNumber(creditStats?.credits_left)}
              testid="credits-left-wrapper"
            />
            <Stat
              label="Included"
              value={formatUsdCompact(included)}
              testid="included-usage-wrapper"
            />
            <Stat
              label="Remaining"
              value={formatUsdCompact(remaining)}
              testid="balance-remaining-wrapper"
              valueClass={remainingColor}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
          <Button
            data-testid="open-mobile-filters"
            variant="outline"
            size="icon"
            className="md:hidden h-9 w-9 rounded-none border-black/20 bg-white text-black hover:bg-black/5 hover:text-black"
            onClick={onOpenMobileFilters}
          >
            <SlidersHorizontal className="w-4 h-4" />
          </Button>

          <Button
            data-testid="connect-api-button"
            variant="outline"
            className="hidden md:inline-flex h-9 rounded-none border-black/20 bg-white text-black hover:bg-black/5 hover:text-black font-mono-plex text-[11px] uppercase tracking-[0.15em]"
            onClick={onConnectApi}
          >
            <Plug className="w-3.5 h-3.5 mr-2" />
            {apiKeyState.connected ? "Manage Key" : "Connect API"}
          </Button>

          <Button
            data-testid="open-logs-button"
            variant="outline"
            className="hidden md:inline-flex h-9 rounded-none border-black/20 bg-white text-black hover:bg-black/5 hover:text-black font-mono-plex text-[11px] uppercase tracking-[0.15em]"
            onClick={onOpenLogs}
          >
            <ScrollText className="w-3.5 h-3.5 mr-2" />
            Logs
          </Button>

          <Button
            data-testid="sync-now-button"
            className="h-9 rounded-none bg-black text-white hover:bg-black/85 font-mono-plex text-[11px] uppercase tracking-[0.15em] px-2.5 md:px-3"
            onClick={onSync}
            disabled={syncState.running}
          >
            {syncState.running ? (
              <Loader2 className="w-3.5 h-3.5 md:mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 md:mr-2" />
            )}
            <span className="hidden md:inline">
              {syncState.running ? "Syncing…" : "Sync Now"}
            </span>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="export-csv-button"
                className="h-9 rounded-none bg-[#007AFF] hover:bg-[#0064D3] text-white font-mono-plex text-[11px] uppercase tracking-[0.15em] px-2.5 md:px-3"
              >
                <Download className="w-3.5 h-3.5 md:mr-2" />
                <span className="hidden md:inline">Export CSV</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="rounded-none bg-white border-black/15 text-black min-w-[220px]"
            >
              <DropdownMenuItem
                data-testid="export-all-item"
                className="font-mono-plex text-xs uppercase tracking-[0.1em] focus:bg-black/5 focus:text-black cursor-pointer rounded-none"
                onClick={onExportAll}
              >
                Export all
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="export-filtered-item"
                disabled={!filterActive}
                className="font-mono-plex text-xs uppercase tracking-[0.1em] focus:bg-black/5 focus:text-black cursor-pointer rounded-none"
                onClick={onExportFiltered}
              >
                Export filtered
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-black/10" />
              <DropdownMenuItem
                data-testid="export-selected-item"
                disabled={selectedCount === 0}
                className="font-mono-plex text-xs uppercase tracking-[0.1em] focus:bg-black/5 focus:text-black cursor-pointer rounded-none"
                onClick={onExportSelected}
              >
                Export selected ({selectedCount})
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="mobile-more-menu"
                variant="ghost"
                size="icon"
                className="md:hidden h-9 w-9 rounded-none text-black/70 hover:text-black hover:bg-black/5"
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="rounded-none bg-white border-black/15 text-black min-w-[200px]"
            >
              <DropdownMenuItem
                data-testid="mobile-connect-item"
                className="font-mono-plex text-xs uppercase tracking-[0.1em] focus:bg-black/5 focus:text-black cursor-pointer rounded-none"
                onClick={onConnectApi}
              >
                <Plug className="w-3.5 h-3.5 mr-2" />
                {apiKeyState.connected ? "Manage Key" : "Connect API"}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="mobile-logs-item"
                className="font-mono-plex text-xs uppercase tracking-[0.1em] focus:bg-black/5 focus:text-black cursor-pointer rounded-none"
                onClick={onOpenLogs}
              >
                <ScrollText className="w-3.5 h-3.5 mr-2" />
                Logs
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-black/10" />
              <DropdownMenuItem
                data-testid="mobile-logout-item"
                className="font-mono-plex text-xs uppercase tracking-[0.1em] focus:bg-black/5 focus:text-black cursor-pointer rounded-none"
                onClick={onLogout}
              >
                <LogOut className="w-3.5 h-3.5 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            data-testid="logout-button"
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex h-9 w-9 rounded-none text-black/60 hover:text-black hover:bg-black/5"
            onClick={onLogout}
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Mobile stats strip — horizontal scroll on very narrow screens */}
      <div className="lg:hidden flex items-center gap-4 px-3 md:px-6 pb-3 pt-1 overflow-x-auto scrollbar-none">
        <StatusBadge syncState={syncState} apiKeyState={apiKeyState} />
        <div className="w-px h-4 bg-black/10 flex-shrink-0" />
        <Stat
          label="Records"
          value={syncState.total_conversations ?? 0}
          testid="records-count"
        />
        <Stat
          label="Credits left"
          value={formatCompactNumber(creditStats?.credits_left)}
          testid="credits-left"
        />
        <Stat
          label="Included"
          value={formatUsdCompact(included)}
          testid="included-usage"
        />
        <Stat
          label="Remaining"
          value={formatUsdCompact(remaining)}
          testid="balance-remaining"
          valueClass={remainingColor}
        />
        <Stat
          label="Last sync"
          value={
            latest?.completed_at
              ? formatDate(latest.completed_at)
              : latest?.started_at
                ? formatDate(latest.started_at)
                : "never"
          }
          testid="last-sync-timestamp"
        />
      </div>
    </header>
  );
}
